import { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useSnapshot } from "valtio";
import {
  CalendarDays,
  BarChart3,
  Menu,
  X,
  LogOut,
  Settings,
} from "lucide-react";
import { store, actions, loadSettings } from "@/lib/store";
import { logout } from "@/lib/api";
import { cn } from "@/lib/utils";
import { useTheme } from "@/lib/use-theme";
import { Button } from "@/components/ui/button";
import { SettingsDialog } from "@/components/SettingsDialog";

const navItems = [
  { to: "/", icon: CalendarDays, label: "Events" },
  { to: "/analytics", icon: BarChart3, label: "Analytics" },
];

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mql = window.matchMedia("(max-width: 767px)");
    setIsMobile(mql.matches);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, []);

  return isMobile;
}

export function Layout() {
  const { sidebarCollapsed, user } = useSnapshot(store);
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const isMobile = useIsMobile();
  useTheme();

  // Load persisted settings on mount
  useEffect(() => {
    loadSettings();
  }, []);

  // Close mobile sidebar on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Auto-collapse sidebar on mobile
  useEffect(() => {
    if (isMobile) {
      actions.setSidebarCollapsed(true);
    }
  }, [isMobile]);

  async function handleLogout() {
    await logout();
    navigate("/login", { replace: true });
  }

  const sidebarContent = (
    <>
      {/* Logo */}
      <div className="flex h-16 items-center justify-between border-b border-organize-border px-4">
        {(!sidebarCollapsed || isMobile) && (
          <span className="text-xl font-bold text-vizimix-400">
            Vizi Fan Mix
          </span>
        )}
        {isMobile ? (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileOpen(false)}
            className="shrink-0"
          >
            <X className="h-5 w-5" />
          </Button>
        ) : (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => actions.toggleSidebar()}
            className="shrink-0"
          >
            {sidebarCollapsed ? (
              <Menu className="h-5 w-5" />
            ) : (
              <X className="h-5 w-5" />
            )}
          </Button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex flex-col gap-1 p-2 flex-1">
        {navItems.map(({ to, icon: Icon, label }) => (
          <NavLink
            key={to}
            to={to}
            end={to === "/"}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                isActive
                  ? "bg-vizimix-600 text-white"
                  : "text-vizimix-300 hover:bg-organize-hover hover:text-vizimix-100"
              )
            }
          >
            <Icon className="h-5 w-5 shrink-0" />
            {(!sidebarCollapsed || isMobile) && <span>{label}</span>}
          </NavLink>
        ))}

      </nav>

      {/* User info, settings, and logout */}
      <div className="border-t border-organize-border p-2">
        {(!sidebarCollapsed || isMobile) && user && (
          <div className="flex items-center gap-2 px-3 py-2 mb-2">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-vizimix-200 truncate">
                {user.displayName || user.email}
              </p>
              <p className="text-xs text-vizimix-400 truncate">{user.email}</p>
            </div>
            <button
              onClick={() => actions.setSettingsOpen(true)}
              className="text-vizimix-400 hover:text-vizimix-200 transition-colors shrink-0"
              title="Settings"
            >
              <Settings className="h-4.5 w-4.5" />
            </button>
          </div>
        )}
        {sidebarCollapsed && !isMobile && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => actions.setSettingsOpen(true)}
            className="text-vizimix-300 hover:text-vizimix-100 w-full mb-1"
            title="Settings"
          >
            <Settings className="h-5 w-5 shrink-0" />
          </Button>
        )}
        <Button
          variant="ghost"
          size={sidebarCollapsed && !isMobile ? "icon" : "default"}
          onClick={handleLogout}
          className={cn(
            "text-vizimix-300 hover:text-vizimix-100",
            (!sidebarCollapsed || isMobile) && "w-full justify-start"
          )}
        >
          <LogOut className="h-5 w-5 shrink-0" />
          {(!sidebarCollapsed || isMobile) && <span className="ml-3">Sign out</span>}
        </Button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex">
      <SettingsDialog />
      {/* Mobile overlay backdrop */}
      {isMobile && mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed left-0 top-0 z-50 h-screen border-r border-organize-border bg-organize-card transition-all duration-300 flex flex-col",
          isMobile
            ? cn("w-64", mobileOpen ? "translate-x-0" : "-translate-x-full")
            : sidebarCollapsed ? "w-16" : "w-64"
        )}
      >
        {sidebarContent}
      </aside>

      {/* Main content */}
      <main
        className={cn(
          "flex-1 transition-all duration-300",
          isMobile ? "ml-0" : sidebarCollapsed ? "ml-16" : "ml-64"
        )}
      >
        {/* Header */}
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-organize-border bg-organize-bg/80 px-4 md:px-6 backdrop-blur">
          <div className="flex items-center gap-3">
            {isMobile && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setMobileOpen(true)}
              >
                <Menu className="h-5 w-5" />
              </Button>
            )}
            <h1 className="text-lg font-semibold text-vizimix-100">
              Event Organizer
            </h1>
          </div>
          {user && (
            <div className="text-sm text-vizimix-400 hidden sm:block">
              Signed in as{" "}
              <span className="text-vizimix-200">{user.email}</span>
            </div>
          )}
        </header>

        {/* Page content */}
        <div className="p-4 md:p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
